import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import {
  SharedProps,
  withDutyCodeSharedProps
} from 'components/shared/DutyCodesGrid/withDutyCodeSharedProps';

import AddOrEditForm from './components/AddOrEditForm';
import { mutations, queries, queryKeys } from 'RestAPIs/rapini';

import { useGetColumnDefs } from './hooks/useGetColumnDefs';
import { useTranslation } from 'react-i18next';
import { showSuccessSnackbar } from 'components/shared/Snackbar';
import { useQueryClient } from 'react-query';
import { showSnackbar } from 'components/shared/index';
import { useCallback, useState } from 'react';
import { DutyCode } from 'generated/index';
import { pipe } from 'ramda';
import {
  FilterComponent,
  Filters
} from '../../Tables/Settings/AdminFunctions/filter/FilterComponent';
import { matchSorter } from 'match-sorter';
import dayjs from 'dayjs';

const DutyCodes = ({ sx, getRowHeight }: SharedProps) => {
  const { t } = useTranslation('common');
  const queryClient = useQueryClient();
  const columns = useGetColumnDefs();
  const { data: rows, isFetching } = queries.useGetDutyCodes(
    undefined,
    undefined,
    true
  );
  const [filteredRows, setFilteredRows] = useState<DutyCode[] | undefined>();
  const [timeHighlight, setTimeHighlight] = useState<boolean>(false);
  const { mutateAsync: handleDelete } = mutations.useDeleteDutyCode();

  const filterBySearch = (data: DutyCode[], search: string | null) => {
    const getTimeFilterRegex = (a = '', b = '') =>
      new RegExp(!a || !b ? `^${a || b}$` : `^(${a})-(${b})$`, 'gi');

    const startAndEndTimeRegex = /^(\d{2}:\d{2})\s?-\s?(\d{2}:\d{2})$/i;

    const filteredOptions = data.filter((option) => {
      const { periods } = option;

      return (periods || []).some((period) => {
        const startTimeString = dayjs(period.startTime).format('HH:mm');
        const endTimeString = dayjs(period.endTime).format('HH:mm');
        if (startAndEndTimeRegex.test(search || '')) {
          const [, start, end] = startAndEndTimeRegex.exec(search || '') || [
            '',
            '',
            ''
          ];
          const time = getTimeFilterRegex(start, end);
          // A specific start and end time is provided
          return time.test(`${startTimeString}-${endTimeString}`);
        } else {
          // A single time that matches either the start or end time
          const time = getTimeFilterRegex(search || '');
          return time.test(startTimeString) || time.test(endTimeString);
        }
      });
    });

    setTimeHighlight(filteredOptions.length > 0);
    return filteredOptions.length > 0
      ? filteredOptions
      : matchSorter(data, search || '', {
          keys: ['dutyCode', 'description']
        });
  };

  const handleFilterChange = useCallback(
    async (filters: Filters) => {
      if (isFetching) return rows;
      const { search } = filters;

      const filtered = await pipe((data) => filterBySearch(data, search))(rows);

      setFilteredRows(filtered);
    },
    [rows]
  );

  return (
    <>
      <FilterComponent
        handleFilterChange={handleFilterChange}
        dependencies={[rows]}
        timeHighlight={timeHighlight}
      />
      <TableCRUDLayout
        loading={isFetching}
        formContainer="drawer"
        FormComponent={AddOrEditForm}
        drawerWidth={700}
        columns={columns}
        rows={filteredRows}
        getRowId={(data) => data.dutyCodeId}
        sx={sx}
        getRowHeight={getRowHeight}
        onDeleteClick={(id) =>
          handleDelete(
            {
              dutyCodeId: id[0] as number
            },
            {
              onSuccess: () => {
                queryClient.invalidateQueries(queryKeys.getDutyCodes.name);
                showSuccessSnackbar();
              },
              onError: (error: any) => {
                const message = t(
                  error?.response?.data?.error?.message || 'somethingWentWrong'
                );
                showSnackbar({
                  severity: 'error',
                  message: message
                });
              }
            }
          )
        }
        canBeDeletedProp="canBeDeleted"
        confirmationTitle={t('adminPortal.deleteDutyCodeDialog.title')}
        confirmationBody={t('adminPortal.deleteDutyCodeDialog.body')}
      />
    </>
  );
};

export default withDutyCodeSharedProps(DutyCodes);
